/**
 * ElevenLabs voices for AI interview — female voices only, grouped by interview language.
 * Server TTS uses `tts_voice_id`; falls back to env default when unset.
 *
 * Curated to align with the shared `elevenlabs-voices` Edge function (Indian fallbacks + multilingual
 * language lists). See B2C `supabase/functions/elevenlabs-voices/index.ts` for reference.
 */
export type ElevenLabsInterviewVoice = {
  id: string;
  label: string;
  hint: string;
};

/** Core multilingual female voices (eleven_multilingual_v2). */
const RACHEL = '21m00Tcm4TlvDq8ikWAM';
const DOMI = 'AZnzlk1XvdvUeBnXmlld';
const BELLA = 'EXAVITQu4vr4xnSDxMaL';
const ELLI = 'MF3mGyEYCl7XYWbV9V6O';
const CHARLOTTE = 'XB0fDUnXU5powFXDhCwa';
const MATILDA = 'XrExE9yKIg1WjnnlVkGX';

/** Indian English female voices — same IDs as `INDIAN_FALLBACK` in elevenlabs-voices (female entries). */
const MEERA = 'nPczCWT5I2Y0J4X0VU8x';
const KAVYA = 'bVMeCyBFy1Xqm3LO4ZN7';
const ANANYA = 'c4bGv7Ty1YqN3LO5ZO8y';
const PRIYA = 'amiAXapsDOAiHJqbsAZj';

const INDIAN_FEMALE_IDS = new Set([MEERA, KAVYA, ANANYA, PRIYA]);

const INDIAN_FEMALE_EN: ElevenLabsInterviewVoice[] = [
  { id: MEERA, label: 'Meera', hint: 'Indian English — clear, natural' },
  { id: KAVYA, label: 'Kavya', hint: 'Indian English — warm, expressive' },
  { id: ANANYA, label: 'Ananya', hint: 'Indian English — friendly, bright' },
  { id: PRIYA, label: 'Priya', hint: 'Indian English — soft, conversational' },
];

function mergeIndianThenCore(
  indian: ElevenLabsInterviewVoice[],
  core: ElevenLabsInterviewVoice[],
): ElevenLabsInterviewVoice[] {
  const seen = new Set<string>(indian.map((v) => v.id));
  const rest = core.filter((v) => !seen.has(v.id) && !INDIAN_FEMALE_IDS.has(v.id));
  return [...indian, ...rest];
}

const CORE_MIXED: ElevenLabsInterviewVoice[] = [
  { id: RACHEL, label: 'Rachel', hint: 'Warm, professional' },
  { id: DOMI, label: 'Domi', hint: 'Strong, steady' },
  { id: BELLA, label: 'Bella', hint: 'Soft, expressive' },
  { id: ELLI, label: 'Elli', hint: 'Friendly, clear' },
  { id: CHARLOTTE, label: 'Charlotte', hint: 'Polished, calm' },
  { id: MATILDA, label: 'Matilda', hint: 'Warm, articulate' },
];

/** English (India): Indian voices first, then global female set. */
const VOICES_EN: ElevenLabsInterviewVoice[] = mergeIndianThenCore(
  INDIAN_FEMALE_EN,
  CORE_MIXED,
);

const VOICES_HI: ElevenLabsInterviewVoice[] = mergeIndianThenCore(
  [
    { id: MEERA, label: 'Meera', hint: 'Hindi — clear delivery' },
    { id: KAVYA, label: 'Kavya', hint: 'Hindi — warm' },
    { id: ANANYA, label: 'Ananya', hint: 'Hindi — bright' },
    { id: PRIYA, label: 'Priya', hint: 'Hindi — conversational' },
  ],
  [
    { id: RACHEL, label: 'Rachel', hint: 'Hindi — warm, clear delivery' },
    { id: DOMI, label: 'Domi', hint: 'Hindi — confident tone' },
    { id: BELLA, label: 'Bella', hint: 'Hindi — soft, expressive' },
    { id: ELLI, label: 'Elli', hint: 'Hindi — friendly pacing' },
    { id: CHARLOTTE, label: 'Charlotte', hint: 'Hindi — polished' },
    { id: MATILDA, label: 'Matilda', hint: 'Hindi — articulate' },
  ],
);

/** Reuse same structure as HI for regional Indian languages (multilingual TTS). */
const VOICES_KN: ElevenLabsInterviewVoice[] = mergeIndianThenCore(
  [
    { id: MEERA, label: 'Meera', hint: 'Kannada context — clear' },
    { id: KAVYA, label: 'Kavya', hint: 'Kannada context — warm' },
    { id: ANANYA, label: 'Ananya', hint: 'Kannada context — bright' },
    { id: PRIYA, label: 'Priya', hint: 'Kannada context — soft' },
  ],
  CORE_MIXED.map((v) => ({ ...v, hint: `Kannada — ${v.label}` })),
);

const VOICES_MR: ElevenLabsInterviewVoice[] = mergeIndianThenCore(
  [
    { id: MEERA, label: 'Meera', hint: 'Marathi context — clear' },
    { id: KAVYA, label: 'Kavya', hint: 'Marathi context — warm' },
    { id: ANANYA, label: 'Ananya', hint: 'Marathi context — bright' },
    { id: PRIYA, label: 'Priya', hint: 'Marathi context — soft' },
  ],
  CORE_MIXED.map((v) => ({ ...v, hint: `Marathi — ${v.label}` })),
);

/** Matches LANG_RU female options from elevenlabs-voices (Rachel lead). */
const VOICES_RU: ElevenLabsInterviewVoice[] = [
  { id: RACHEL, label: 'Rachel', hint: 'Russian — warm' },
  { id: BELLA, label: 'Bella', hint: 'Russian — expressive' },
  { id: DOMI, label: 'Domi', hint: 'Russian — steady' },
  { id: ELLI, label: 'Elli', hint: 'Russian — clear' },
  { id: CHARLOTTE, label: 'Charlotte', hint: 'Russian — calm' },
  { id: MATILDA, label: 'Matilda', hint: 'Russian — articulate' },
];

/** LANG_PT: Rachel, Bella first (same order as elevenlabs-voices). */
const VOICES_PT: ElevenLabsInterviewVoice[] = [
  { id: RACHEL, label: 'Rachel', hint: 'Portuguese — warm' },
  { id: BELLA, label: 'Bella', hint: 'Portuguese — expressive' },
  { id: DOMI, label: 'Domi', hint: 'Portuguese — steady' },
  { id: ELLI, label: 'Elli', hint: 'Portuguese — clear' },
  { id: CHARLOTTE, label: 'Charlotte', hint: 'Portuguese — calm' },
  { id: MATILDA, label: 'Matilda', hint: 'Portuguese — articulate' },
];

/** LANG_ES: Domi first (elevenlabs-voices). */
const VOICES_ES: ElevenLabsInterviewVoice[] = [
  { id: DOMI, label: 'Domi', hint: 'Spanish — steady, clear' },
  { id: BELLA, label: 'Bella', hint: 'Spanish — expressive' },
  { id: RACHEL, label: 'Rachel', hint: 'Spanish — warm' },
  { id: ELLI, label: 'Elli', hint: 'Spanish — friendly' },
  { id: CHARLOTTE, label: 'Charlotte', hint: 'Spanish — calm' },
  { id: MATILDA, label: 'Matilda', hint: 'Spanish — articulate' },
];

const VOICES_HINGLISH: ElevenLabsInterviewVoice[] = mergeIndianThenCore(
  [
    { id: PRIYA, label: 'Priya', hint: 'Hinglish — conversational' },
    { id: MEERA, label: 'Meera', hint: 'Hinglish — clear mix' },
    { id: KAVYA, label: 'Kavya', hint: 'Hinglish — warm mix' },
    { id: ANANYA, label: 'Ananya', hint: 'Hinglish — bright' },
  ],
  [
    { id: ELLI, label: 'Elli', hint: 'Hinglish — friendly' },
    { id: RACHEL, label: 'Rachel', hint: 'Hinglish — warm' },
    { id: BELLA, label: 'Bella', hint: 'Hinglish — soft blend' },
    { id: DOMI, label: 'Domi', hint: 'Hinglish — confident' },
    { id: CHARLOTTE, label: 'Charlotte', hint: 'Hinglish — polished' },
    { id: MATILDA, label: 'Matilda', hint: 'Hinglish — natural' },
  ],
);

const ALL_VOICE_GROUPS = [
  VOICES_EN,
  VOICES_HI,
  VOICES_KN,
  VOICES_MR,
  VOICES_RU,
  VOICES_PT,
  VOICES_ES,
  VOICES_HINGLISH,
];

export function voicesForInterviewLanguage(language: string): ElevenLabsInterviewVoice[] {
  const c = (language || 'en-IN').toLowerCase();
  if (c === 'hi-en' || c.includes('hinglish')) return VOICES_HINGLISH;
  if (c === 'hi-in' || (c.startsWith('hi') && !c.includes('hinglish'))) return VOICES_HI;
  if (c.startsWith('kn')) return VOICES_KN;
  if (c.startsWith('mr')) return VOICES_MR;
  if (c.startsWith('ru')) return VOICES_RU;
  if (c.startsWith('pt')) return VOICES_PT;
  if (c.startsWith('es')) return VOICES_ES;
  if (c === 'en-us') return VOICES_EN;
  if (c.startsWith('en')) return VOICES_EN;
  return VOICES_EN;
}

export function defaultVoiceForLanguage(language: string): string {
  const list = voicesForInterviewLanguage(language);
  return list[0]?.id ?? MEERA;
}

export const DEFAULT_ELEVENLABS_VOICE_ID = defaultVoiceForLanguage('en-IN');

/** Unique voice ids (Set — use `.has()` for membership). */
export const ALL_ELEVENLABS_VOICE_IDS = new Set(ALL_VOICE_GROUPS.flatMap((g) => g.map((v) => v.id)));

export function isKnownElevenLabsVoiceId(id: string): boolean {
  return ALL_ELEVENLABS_VOICE_IDS.has(id);
}
